import { CategoryMetas } from "@hermes/api-model-category";
import { CTAToken } from "@hermes/api-model-common";
import { GridProduct } from "@hermes/api-model-product";

import { MissingFacetContext } from "../constants/missing-facet-context.constant";

export enum Sort {
    Relevance = "relevance",
    PriceAsc = "priceasc",
    PriceDesc = "pricedsc",
}

export const DIRECT = "direct";
export const DEFAULT_SORT = Sort.Relevance;
export const FH_RANGE_PRICE_PARAM = "fh_range_price";
export const FH_SORT_BY_REGEX = /&fh_sort_by=([^&]+)/gm;
export const FH_LOCATION_REGEX = "fh_location";
export type PageType = "product-page" | "grid-page" | "other-page";
export type PriceType = "indicative" | "fixed";
export const GRID_PAGE = "grid-page";
export const PRODUCT_PAGE = "product-page";
export const OTHER_PAGE = "other-page";

export interface CombinedFacets {
    facetName: string;
    facetNameValue: string;
}

export interface SelectedFilter {
    name: string;
    value: string;
}

export interface RouteFacet {
    facetName: string;
    facetNameValue: string;
    combinedFacets: CombinedFacets[];
    enabled: boolean;
    updateMetas: boolean;
}

export interface PriceRangeCurrent {
    valueMinCurrent: number;
    valueMaxCurrent: number;
}

export interface PriceRange extends PriceRangeCurrent {
    valueMin: number;
    valueMax: number;
    valueChanged?: string;
}

export enum GridType {
    Search = "search",
    Category = "category",
    Facet = "facet",
}

export interface GridSortByOption {
    sort: Sort;
    label: string;
}

export interface GridFetchResult {
    gridData: GridData;
    missingFacetContext?: MissingFacetContext;
}

export interface CurrentRouteData {
    facet: RouteFacet;
    gridType: GridType;
    pageNumber: number;
    prices: PriceRange | undefined;
    searchTerm: string | undefined;
    sortBy: Sort;
    urlParams: string;
}

export interface BackToGridProperties {
    productSku?: string;
    positionInPage: number;
}

export interface GridNavigationData {
    pagesLoaded: number;
    previousCategoryPimCode: string;
    previousPage: PageType;
    isFromPreviousGrid: boolean;
    backToGridProductSkuOrigin?: string;
    currentPage: number;
    isLastPage: boolean;
    totalPages: number;
    realTotalProducts: number;
}

export interface GridCategory {
    pimCode: string;
    name: string;
    masterCategoryName: string;
}

export interface GridMenuLevel {
    menuLv1: string;
    menuLv2?: string;
    menuLv3?: string;
}

export interface CategoryRouteData {
    category: GridCategory | undefined;
    menuLevel: GridMenuLevel;
}

export interface GridLoadMoreState {
    pending: boolean;
    activated: boolean;
}

export interface GridFilter {
    name: string;
    selected: boolean;
    total: number;
    urlParams?: string;
    value: string;
    filterGroupName: string;
    facetIsCrawlable: boolean;
    combinedFilterImage?: string;
    gridHighlightImage?: string;
    filterImage?: string;
}

export interface GridFilterGroup {
    name: string;
    attribute: string;
    displayHint: string;
    presentation: string;
    isGridHighlight: boolean;
    isHiddenInFilterBar?: boolean;
    facetUrlParameterName: string;
    seoPriority?: number;
    filters: GridFilter[];
    priceRange?: PriceRange;
}

export interface GridHighlight {
    filter: GridFilter;
    filterGroup: GridFilterGroup;
}

/**
 * hasMoreColors => moreColors ? (à vérifier)
 * isHero
 * gridAssetsVariants
 * quickBuyProperties
 */

export interface GridEdito {
    nodeId: number;
    mediaType: string;
    contentTitle: string;
    contentBody: string;
    contentTokens?: CTAToken[];
    nodeUrl: string;
    imageDesktopUrl: string;
    imageDesktopAlt: string;
    imageMobileUrl: string;
    imageMobileAlt: string;
    videoUrl: string;
    videoAlt: string;
    videoLoopMuteAutoplay: boolean;
    alwaysShowControls: boolean;
    imageRetouched: boolean;
    token?: CTAToken;
}

export interface GridAlternatives {
    originalSearch: string;
    resultsFor: string;
    total: number;
}

export interface AltPaths {
    [locale: string]: string;
}

export interface GridFacet {
    title: string;
    name: string;
    path: string;
    altPaths: AltPaths;
    error?: string;
}

export interface GridData {
    total: number;
    products: GridProduct[];
    productsByCategory?: Record<string, GridProduct[]>;
    filterGroups: GridFilterGroup[];
    editos: GridEdito[];
    alternatives: GridAlternatives;
    redirectionUrl: string;
    url: string;
    facet: GridFacet;
    isHeroProduct: boolean;
}

export interface GridState {
    categoryRouteData: CategoryRouteData;
    currentRouteData: CurrentRouteData;
    rawCategoryMetas: CategoryMetas | undefined;
    pendingCategoryMetas: boolean;
    rawGridData: GridData | undefined;
    filterGroupsWithCrawlability: GridFilterGroup[] | undefined;
    pendingGridData: boolean;
    navigationData: GridNavigationData;
    loadMore: GridLoadMoreState;
    pendingUrlUpdate: boolean;
}

export const initialNavigationData: GridNavigationData = {
    pagesLoaded: 1,
    previousPage: GRID_PAGE,
    previousCategoryPimCode: "",
    isFromPreviousGrid: false,
    backToGridProductSkuOrigin: undefined,
    currentPage: 1,
    isLastPage: false,
    totalPages: 0,
    realTotalProducts: 0,
};

export const initialRouteFacet: RouteFacet = {
    facetName: "",
    facetNameValue: "",
    combinedFacets: [],
    enabled: false,
    updateMetas: false,
};

export interface Facet {
    facetName: string;
    facetNameValue: string;
    combinedFacets: CombinedFacets[];
    facetDisplayName: string;
    facetTitle: string;
    updateMetas: boolean;
    altPaths?: AltPaths;
    enabled: boolean;
    path: string;
}

export const initialFacet: Facet = {
    ...initialRouteFacet,
    combinedFacets: [...initialRouteFacet.combinedFacets],
    facetDisplayName: "",
    facetTitle: "",
    altPaths: {},
    path: "",
};

export const initialLoadMore: GridLoadMoreState = {
    pending: false,
    activated: false,
};

export const initialState: GridState = {
    categoryRouteData: {
        category: undefined,
        menuLevel: { menuLv1: DIRECT },
    },
    currentRouteData: {
        facet: initialRouteFacet,
        gridType: GridType.Category,
        pageNumber: 1,
        prices: undefined,
        searchTerm: undefined,
        sortBy: Sort.Relevance,
        urlParams: "",
    },
    rawCategoryMetas: undefined,
    pendingCategoryMetas: false,
    rawGridData: undefined,
    filterGroupsWithCrawlability: undefined,
    pendingGridData: false,
    navigationData: initialNavigationData,
    loadMore: initialLoadMore,
    pendingUrlUpdate: false,
};
